import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { observer } from "mobx-react-lite";
import {
  Search,
  MapPin,
  ChevronDown,
  Plus,
  Minus,
  X as XIcon,
} from "lucide-react";
import DateRangePicker from "./DateRangePicker";
import searchStore from "../store/searchStore";
import { logoLinks } from "./logoLinks";

const SpravSearchBar = observer(function SpravSearchBar({
  page = "",
  variant = "hero",
  initialDestination = "",
  searchPlaceholder = "Куда вы хотите поехать?",
  searchSubtitle = "12 027 отелей",
}) {
  const navigate = useNavigate();

  const [showAgeDropdown, setShowAgeDropdown] = useState(false);
  const [guestsOpen, setGuestsOpen] = useState(false);
  // const [destinationFocused, setDestinationFocused] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [scrolledPanelOpen, setScrolledPanelOpen] = useState(false);
  const [mobileDatesVisible, setMobileDatesVisible] = useState(true);
  const inputRef = useRef(null);
  const guestsRef = useRef(null);
  const ageDropdownRef = useRef(null);
  const mobileBarRef = useRef(null);

  // Инициализация дат при монтировании компонента
  // useEffect(() => {
  //   if (!searchStore.dateCheckin && !searchStore.dateCheckout) {
  //     const today = new Date();
  //     const defIn = new Date(today);
  //     defIn.setDate(today.getDate() + 7);
  //     const defOut = new Date(today);
  //     defOut.setDate(today.getDate() + 9);
  //     const toISO = (d) => d.toISOString().slice(0, 10);

  //     searchStore.setDateCheckin(toISO(defIn));
  //     searchStore.setDateCheckout(toISO(defOut));
  //   }
  // }, []);

  // Используем searchStore для поиска
  const showSuggestions =
    searchStore.isOnFocus &&
    searchStore.query.length >= 2 &&
    (searchStore.isSearching ||
      searchStore.regions.length > 0 ||
      searchStore.cities.length > 0 ||
      searchStore.places.length > 0 ||
      searchStore.hotels.length > 0);

  const handleSelectSuggestion = (item, type) => {
    if (type === "region") {
      searchStore.setSelectedRegion(item.url, item.name);
      searchStore.setHotelsCount(item.hotels);
      searchStore.setHotelsTotalCount(item.hotels_total || 0);
      searchStore.setIsOnFocus(false);
      searchStore.setQuery("");
      searchStore.setPlaceholder(item.name);
      if (item.url !== "all") {
        navigate(`/russia/${item.url}`);
      } else {
        navigate("/");
      }
    } else if (type === "city") {
      searchStore.setSelectedCity(
        item.city_url || item.url,
        item.city || item.name,
      );
      searchStore.setHotelsCount(item.hotels);
      searchStore.setHotelsTotalCount(item.hotels_total || 0);
      searchStore.setIsOnFocus(false);
      searchStore.setQuery("");
      searchStore.setPlaceholder(item.city || item.name);
      if (item.city_url !== "all") {
        navigate(
          `/russia/${item.region_url || searchStore.selectedRegion.url}/${item.city_url}`,
        );
      } else {
        navigate(
          `/russia/${item.region_url || searchStore.selectedRegion.url}`,
        );
      }
    } else if (type === "place") {
      searchStore.setSelectedRegion(item.region_url, item.region);
      searchStore.setSelectedCity(item.city_url, item.city);
      searchStore.setHotelsCount(item.hotels);
      searchStore.setHotelsTotalCount(item.hotels_total || 0);
      searchStore.setSelectedPlace(item);
      searchStore.setQuery("");
      searchStore.setIsOnFocus(false);
      searchStore.setPlaceholder(item.name);
      navigate(`/russia/${item.region_url}/${item.city_url}/${item.place_url}`);
    } else if (type === "hotel") {
      searchStore.setIsOnFocus(false);
      searchStore.setQuery("");
      searchStore.setPlaceholder(item.name);
      navigate(item.url);
    }
  };

  const handleSearch = () => {
    if (page == "hotel") {
      // страница отеля, при поиске просто обновляем страницу
      window.location.reload();
    } else if (
      searchStore.query &&
      (searchStore.regions.length > 0 ||
        searchStore.cities.length > 0 ||
        searchStore.places.length > 0 ||
        searchStore.hotels.length > 0)
    ) {
      // Если есть результаты поиска - переходим на первый
      if (searchStore.regions.length > 0) {
        handleSelectSuggestion(searchStore.regions[0], "region");
      } else if (searchStore.cities.length > 0) {
        handleSelectSuggestion(searchStore.cities[0], "city");
      } else if (searchStore.places.length > 0) {
        handleSelectSuggestion(searchStore.places[0], "place");
      } else if (searchStore.hotels.length > 0) {
        handleSelectSuggestion(searchStore.hotels[0], "hotel");
      }
    } else {
      // Обычный поиск - переходим на /russia
      const params = new URLSearchParams();
      if (searchStore.query) params.set("q", searchStore.query);
      if (searchStore.dateCheckin)
        params.set("checkIn", searchStore.dateCheckin);
      if (searchStore.dateCheckout)
        params.set("checkOut", searchStore.dateCheckout);
      if (searchStore.guestsCount)
        params.set("adults", searchStore.guestsCount);
      if (searchStore.guestsChildren.length > 0)
        params.set("children", JSON.stringify(searchStore.guestsChildren));
      if (searchStore.dogFriendly) params.set("pet", "true");
      navigate(`/russia?${params.toString()}`);
    }
  };

  // Получаем значения из store
  const checkIn = searchStore.dateCheckin;
  const checkOut = searchStore.dateCheckout;
  const adults = searchStore.guestsCount;
  const childrenAges = searchStore.guestsChildren;
  const withPet = searchStore.dogFriendly;

  // Функции для обновления значений
  const handleAdultsChange = (newValue) => {
    searchStore.setGuestsCount(newValue);
  };

  const handleChildrenAgesChange = (newAges) => {
    searchStore.setGuestChildren(newAges);
  };

  const handleAddChild = (age) => {
    searchStore.addGuestsChildren(age);
    setShowAgeDropdown(false);
  };

  const handleRemoveChild = (index) => {
    searchStore.delGuestsChildren(index);
  };

  const handlePetToggle = () => {
    searchStore.setDogFriendly(!withPet);
  };

  useEffect(() => {
    if (variant !== "hero") return;
    const handleScroll = () => setIsScrolled(window.scrollY > 100);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [variant]);

  useEffect(() => {
    function handleOutside(e) {
      if (guestsRef.current && !guestsRef.current.contains(e.target)) {
        setGuestsOpen(false);
      }
      if (mobileBarRef.current && !mobileBarRef.current.contains(e.target))
        setScrolledPanelOpen(false);
      if (ageDropdownRef.current && !ageDropdownRef.current.contains(e.target))
        setShowAgeDropdown(false);
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, []);

  const guestsLabel = [
    `${adults} ${adults === 1 ? "гость" : adults < 5 ? "гостя" : "гостей"}`,
    childrenAges.length
      ? `${childrenAges.length} ${childrenAges.length === 1 ? "ребёнок" : "детей"}`
      : null,
    withPet ? "🐕" : null,
  ]
    .filter(Boolean)
    .join(", ");

  const LogoDropdownInline = () => {
    const [open, setOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
      function handleOutside(e) {
        if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
          setOpen(false);
        }
      }
      document.addEventListener("mousedown", handleOutside);
      return () => document.removeEventListener("mousedown", handleOutside);
    }, []);

    const breadcrumbs = {
      "": [{ label: "Гостим", href: "/" }],
      Sprav: [
        { label: "Гостим", href: "/" },
        { label: "Поиск отелей", href: "/russia" },
      ],
    };

    const currentPage = variant === "russia" ? "russia" : "";
    const items = breadcrumbs[currentPage] || breadcrumbs[""];

    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex items-center hover:opacity-80 transition-opacity flex-shrink-0"
        >
          <img
            src="/favicon-120x120.png"
            alt="Гостим"
            className="h-8 sm:h-9 w-auto object-contain"
          />
        </button>

        {open && (
          <div className="absolute top-full left-0 mt-1 z-[9999] bg-white border border-gray-200 rounded-xl shadow-xl p-3 w-64">
            <div className="pb-3 border-b border-gray-100">
              <div className="flex items-center gap-2 text-xs flex-wrap">
                {items.map((item, idx) => (
                  <React.Fragment key={idx}>
                    <a
                      href={item.href}
                      className="text-gray-600 hover:text-blue-600 transition-colors"
                    >
                      {item.label}
                    </a>
                    {idx < items.length - 1 && (
                      <span className="text-gray-300">/</span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
            <div className="pt-3 space-y-2">
              {logoLinks.map((link) => {
                const Icon = link.icon;
                return (
                  <a
                    key={link.href}
                    href={link.href}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg text-xs text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <Icon className="w-3.5 h-3.5 text-gray-400" />
                    {link.label}
                  </a>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  const GuestsDropdown = ({ anchor }) => (
    <div
      className={`bg-white border border-gray-200 rounded-xl shadow-xl p-4 z-[9999] space-y-3 ${
        anchor === "mobile"
          ? "fixed left-1/2 transform -translate-x-1/2 top-20 w-[calc(100vw-2rem)] max-w-md"
          : "absolute top-full mt-2 right-0 w-72"
      }`}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-gray-900">Взрослые</div>
          <div className="text-xs text-gray-400">старше 18 лет</div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleAdultsChange(Math.max(1, adults - 1));
            }}
            className="w-7 h-7 rounded-full border border-gray-600 text-gray-700 flex items-center justify-center hover:bg-gray-700 hover:text-white hover:border-gray-700 transition-colors"
          >
            <Minus className="w-3 h-3" />
          </button>
          <span className="text-sm font-semibold w-6 text-center text-gray-900">
            {adults}
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleAdultsChange(adults + 1);
            }}
            className="w-7 h-7 rounded-full border border-gray-600 text-gray-700 flex items-center justify-center hover:bg-gray-700 hover:text-white hover:border-gray-700 transition-colors"
          >
            <Plus className="w-3 h-3" />
          </button>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative" ref={ageDropdownRef}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowAgeDropdown((v) => !v);
            }}
            className="text-gray-500 hover:text-gray-700 text-sm flex items-center gap-1"
          >
            <Plus className="w-3.5 h-3.5" />
            Ребёнок
          </button>
          {showAgeDropdown && (
            <div className="absolute left-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg py-1 w-36 max-h-52 overflow-y-auto">
              {Array.from({ length: 17 }, (_, i) => i + 1).map((age) => (
                <button
                  key={age}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAddChild(age);
                  }}
                  className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                >
                  {age} {age === 1 ? "год" : age < 5 ? "года" : "лет"}
                </button>
              ))}
            </div>
          )}
        </div>
        {childrenAges.map((age, idx) => (
          <span
            key={idx}
            className="flex items-center gap-1 bg-gray-100 rounded-full px-2.5 py-1 text-sm text-gray-700"
          >
            {age} {age === 1 ? "год" : age < 5 ? "года" : "лет"}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveChild(idx);
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <XIcon className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          handlePetToggle();
        }}
        className={`text-sm transition-colors ${withPet ? "text-gray-900 font-medium" : "text-gray-500"}`}
      >
        {withPet ? "Едем с собакой 🐕" : "Едем без собаки"}
      </button>
    </div>
  );

  const SuggestionsDropdown = () => (
    <div className="absolute left-0 right-0 top-full mt-1 z-[10000] bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden max-h-96 overflow-y-auto">
      {searchStore.isSearching && (
        <div className="px-4 py-3 text-sm text-gray-500 animate-pulse">
          Поиск...
        </div>
      )}
      {!searchStore.isSearching &&
        searchStore.query.length >= 2 &&
        searchStore.regions.length === 0 &&
        searchStore.cities.length === 0 &&
        searchStore.places.length === 0 &&
        searchStore.hotels.length === 0 && (
          <div className="px-4 py-3 text-sm text-gray-500">
            Ничего не найдено...
          </div>
        )}
      {(searchStore.regions?.length > 0 || searchStore.cities?.length > 0) && (
        <p className="px-4 py-2 text-xs text-gray-400 font-medium">
          Регионы и города:
        </p>
      )}
      {searchStore.regions?.map((region, i) => (
        <button
          key={`region-${i}`}
          onMouseDown={() => handleSelectSuggestion(region, "region")}
          className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-left transition-colors"
        >
          <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <div>
            <div className="text-sm font-medium text-gray-800">
              {region.name}
            </div>
            {region.hotels > 0 && (
              <div className="text-xs text-gray-400">
                {region.hotels} отелей
              </div>
            )}
          </div>
        </button>
      ))}
      {searchStore.cities?.map((city, i) => (
        <button
          key={`city-${i}`}
          onMouseDown={() => handleSelectSuggestion(city, "city")}
          className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-left transition-colors"
        >
          <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <div>
            <div className="text-sm font-medium text-gray-800">{city.city}</div>
            {city.region && (
              <div className="text-xs text-gray-400">{city.region}</div>
            )}
          </div>
        </button>
      ))}
      {searchStore.places?.length > 0 && (
        <p className="px-4 py-2 text-xs text-gray-400 font-medium">Места:</p>
      )}
      {searchStore.places?.map((place, i) => (
        <button
          key={`place-${i}`}
          onMouseDown={() => handleSelectSuggestion(place, "place")}
          className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-left transition-colors"
        >
          <MapPin className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <div>
            <div className="text-sm font-medium text-gray-800">
              {place.name}
            </div>
            {place.city && (
              <div className="text-xs text-gray-400">{place.city}</div>
            )}
          </div>
        </button>
      ))}
      {searchStore.hotels?.length > 0 && (
        <>
          <p className="px-4 py-2 text-xs text-gray-400 font-medium">
            Отели {searchStore.selectedCity.name}:
          </p>
          {searchStore.hotels.map((hotel, i) => (
            <button
              key={`hotel-${i}`}
              onMouseDown={() => handleSelectSuggestion(hotel, "hotel")}
              className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-left transition-colors"
            >
              <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <div>
                <div className="text-sm font-medium text-gray-800">
                  {hotel.name}
                </div>
                {hotel.address && (
                  <div className="text-xs text-gray-400">{hotel.address}</div>
                )}
              </div>
            </button>
          ))}
        </>
      )}
    </div>
  );

  const isSprav = variant === "sprav";
  const bgStyle = {
    background: "linear-gradient(135deg, #020c1b 0%, #0d2137 100%)",
  };

  return (
    <div className="relative max-w-3xl mx-auto w-full">
      {/* Mobile bar */}
      <div
        ref={mobileBarRef}
        className={`${isSprav ? "block sm:hidden" : "sm:hidden fixed top-0 left-0 right-0 z-[9999]"} px-0 py-0`}
        style={isSprav ? {} : bgStyle}
      >
        <div className="relative">
          <div
            className="flex items-center bg-white rounded-full shadow-lg gap-0 h-12"
            style={{ paddingLeft: "6px", paddingRight: "6px" }}
          >
            <div
              className="flex items-center flex-shrink-0 pl-1.5 cursor-pointer"
              onClick={(e) => e.stopPropagation()}
            >
              <LogoDropdownInline />
            </div>
            <div
              className="flex items-center gap-3 px-3 py-1.5 flex-1 min-w-0 text-left cursor-text"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setScrolledPanelOpen(false);
                searchStore.setIsOnFocus(true);
                setTimeout(() => {
                  if (inputRef.current) {
                    inputRef.current.focus();
                    inputRef.current.click();
                  }
                }, 0);
              }}
            >
              <div className="flex-1 min-w-0 h-8 flex flex-col justify-center cursor-text">
                {searchStore.isOnFocus ? (
                  <input
                    ref={inputRef}
                    type="text"
                    value={searchStore.query}
                    onChange={searchStore.onChangeQuery}
                    onBlur={searchStore.onBlurHandler}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSearch();
                    }}
                    placeholder="Регион, город или отель"
                    className="w-full text-base text-gray-800 font-medium outline-none bg-transparent placeholder-gray-400"
                    style={{ fontSize: "16px" }}
                    autoComplete="off"
                    autoFocus
                  />
                ) : (
                  <>
                    <div className="text-gray-800 text-sm font-medium truncate">
                      {searchStore.query ||
                        searchStore.placeholder ||
                        (page === "hotel" ? "" : searchPlaceholder)}
                    </div>
                    <div className="text-gray-400 text-xs">
                      {searchStore.hotelsCount > 0
                        ? `${searchStore.hotelsCount} отелей`
                        : searchSubtitle}
                    </div>
                  </>
                )}
              </div>
            </div>
            <button onClick={handleSearch} className="flex-shrink-0">
              <span
                className="flex items-center justify-center w-9 h-9 rounded-full text-white cursor-pointer"
                style={{
                  background: "linear-gradient(to right, #14b8a6, #3b82f6)",
                }}
              >
                <Search className="w-4 h-4" />
              </span>
            </button>
          </div>
          {showSuggestions && <SuggestionsDropdown />}
        </div>

        {/* Даты и гости - всегда видимы на мобильной версии */}
        <AnimatePresence>
          {!searchStore.isOnFocus && mobileDatesVisible && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="flex items-center justify-center gap-1 mt-2"
            >
              <div className="relative z-[9999]">
                <DateRangePicker
                  checkIn={checkIn}
                  checkOut={checkOut}
                  onChange={({ checkIn: ci, checkOut: co }) => {
                    searchStore.setDateCheckin(ci);
                    searchStore.setDateCheckout(co);
                  }}
                  accentColor="#14b8a6"
                  triggerVariant="pill-light"
                  closeSignal={guestsOpen || undefined}
                  onOpen={() => setGuestsOpen(false)}
                />
              </div>
              <div className="w-px h-4 bg-white/30" />
              <div className="relative flex-shrink-0" ref={guestsRef}>
                <button
                  onClick={() => setGuestsOpen((v) => !v)}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-full transition-colors text-white/90 hover:bg-white/10"
                >
                  <span className="text-sm whitespace-nowrap">
                    {guestsLabel}
                  </span>
                  <ChevronDown className="w-3.5 h-3.5 text-white/60" />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Mobile Guests Modal */}
      <AnimatePresence>
        {guestsOpen && window.innerWidth < 640 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[10001] sm:hidden"
          >
            <div
              className="absolute inset-0 bg-black/50"
              onClick={() => setGuestsOpen(false)}
            />
            <div className="absolute left-4 right-4 top-20 mx-auto max-w-md">
              <GuestsDropdown anchor="mobile" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Desktop pill row */}
      <div
        className={`${isSprav ? "hidden sm:block" : "hidden sm:block"} relative my-1`}
      >
        <div
          className="flex items-center bg-white rounded-full shadow-xl gap-0 h-12"
          style={{ paddingLeft: "6px", paddingRight: "6px" }}
        >
          <div className="flex items-center flex-shrink-0">
            <LogoDropdownInline />
          </div>
          <div
            className="flex items-center gap-3 px-4 py-1.5 flex-1 min-w-0 cursor-text text-left"
            onClick={() => {
              searchStore.setIsOnFocus(true);
            }}
          >
            <div className="flex-1 min-w-0 h-10 flex flex-col justify-center">
              {searchStore.isOnFocus ? (
                <input
                  ref={inputRef}
                  type="text"
                  value={searchStore.query}
                  onChange={searchStore.onChangeQuery}
                  onBlur={searchStore.onBlurHandler}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSearch();
                  }}
                  placeholder="Регион, город или отель"
                  className="w-full text-sm text-gray-800 font-medium outline-none bg-transparent placeholder-gray-400"
                  autoComplete="off"
                  autoFocus
                />
              ) : (
                <>
                  <div className="text-gray-800 text-sm font-medium">
                    {searchStore.query ||
                      searchStore.placeholder ||
                      (page === "hotel" ? "" : searchPlaceholder)}
                  </div>
                  <div className="text-gray-400 text-xs">
                    {searchStore.hotelsCount > 0
                      ? `${searchStore.hotelsCount} отелей`
                      : searchSubtitle}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="hidden sm:block flex-shrink-0 px-2 relative z-[9999]">
            <DateRangePicker
              checkIn={checkIn}
              checkOut={checkOut}
              onChange={({ checkIn: ci, checkOut: co }) => {
                searchStore.setDateCheckin(ci);
                searchStore.setDateCheckout(co);
              }}
              accentColor="#14b8a6"
              triggerVariant="pill"
              closeSignal={guestsOpen || undefined}
              onOpen={() => setGuestsOpen(false)}
            />
          </div>

          <div
            className="hidden sm:block relative flex-shrink-0"
            ref={guestsRef}
          >
            <button
              onClick={() => setGuestsOpen((v) => !v)}
              className="flex items-center gap-1.5 px-4 py-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <span className="text-sm text-gray-700 whitespace-nowrap">
                {guestsLabel}
              </span>
              <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
            </button>
            {guestsOpen && <GuestsDropdown anchor="desktop" />}
          </div>

          <button onClick={handleSearch} className="flex-shrink-0">
            <span
              className="block px-5 rounded-full text-white font-semibold text-sm cursor-pointer"
              style={{
                background: "linear-gradient(to right, #14b8a6, #3b82f6)",
                paddingTop: "8px",
                paddingBottom: "8px",
              }}
            >
              Найти
            </span>
          </button>
        </div>
        {showSuggestions && <SuggestionsDropdown />}
      </div>
    </div>
  );
});

export default SpravSearchBar;
